import type { DataProvider, TradingPair } from 'hopcharts';
import { DEFAULT_EXCHANGE, EXCHANGES } from './exchanges';

/** One market: exchange id + canonical BASE/QUOTE pair. */
export interface SymbolRef {
  exchangeId: string;
  pair: string;
}

/** Storage key, e.g. "binance:BTC/USDT". */
export function symbolKey(ref: SymbolRef): string {
  return `${ref.exchangeId}:${ref.pair}`;
}

export function fromSymbolKey(key: string): SymbolRef | null {
  const i = key.indexOf(':');
  if (i <= 0) return null;
  const exchangeId = key.slice(0, i);
  const pair = key.slice(i + 1);
  return pair.includes('/') ? { exchangeId, pair } : null;
}

/** Ticker as traders write it: "BTCUSDT". */
export function tickerOf(pair: string): string {
  return pair.replace('/', '');
}

/** TradingView-style id: "BINANCE:BTCUSDT". */
export function tvSymbol(ref: SymbolRef): string {
  return `${ref.exchangeId.toUpperCase()}:${tickerOf(ref.pair)}`;
}

export function baseOf(pair: string): string {
  return pair.split('/')[0] ?? pair;
}

export function quoteOf(pair: string): string {
  return pair.split('/')[1] ?? '';
}

const pairCache = new Map<string, Promise<TradingPair[]>>();

/** Pair list per exchange, loaded once per session (the provider caches too). */
export function loadPairs(provider: DataProvider, exchangeId: string): Promise<TradingPair[]> {
  let pending = pairCache.get(exchangeId);
  if (!pending) {
    pending = provider.getPairs ? provider.getPairs(exchangeId) : Promise.resolve([]);
    pending.catch(() => pairCache.delete(exchangeId));
    pairCache.set(exchangeId, pending);
  }
  return pending;
}

function normalizeTicker(raw: string): string {
  return raw.toUpperCase().replace(/[-_/\s]/g, '');
}

// Kraken's legacy asset codes, still used in TradingView-style symbols (KRAKEN:XBTEUR); the pair
// lists use the common ones, as the HopCharts adapter maps them.
const LEGACY_ASSETS: Record<string, string> = { XBT: 'BTC', XDG: 'DOGE' };

/** The ticker itself first, then with a legacy code as base or quote replaced ("ETHXBT" → "ETHBTC"). */
function tickerCandidates(ticker: string): string[] {
  const out = [ticker];
  for (const [legacy, common] of Object.entries(LEGACY_ASSETS)) {
    if (ticker.startsWith(legacy)) out.push(common + ticker.slice(legacy.length));
    if (ticker.endsWith(legacy)) out.push(ticker.slice(0, -legacy.length) + common);
  }
  return out;
}

/**
 * Resolve user / URL input into a market: "BINANCE:BTCUSDT", "binance:BTC/USDT",
 * "BTC/USDT", "btcusdt", "BTC-EUR". Without an exchange the default exchange is
 * tried first, then the others. Null when nothing matches. Throws when nothing matches and a
 * market list needed for the answer could not be loaded: "unreachable" is not "does not exist".
 */
export async function resolveSymbol(provider: DataProvider, input: string): Promise<SymbolRef | null> {
  const raw = input.trim();
  if (!raw) return null;
  let exchangeHint: string | null = null;
  let ticker = raw;
  const colon = raw.indexOf(':');
  if (colon > 0) {
    exchangeHint = raw.slice(0, colon).toLowerCase();
    ticker = raw.slice(colon + 1);
  }
  // No market on an exchange the site does not chart; asking would look like an unreachable exchange.
  if (exchangeHint && !EXCHANGES.some((e) => e.id === exchangeHint)) return null;
  const wanted = tickerCandidates(normalizeTicker(ticker));
  const explicitPair = /[/\-_]/.test(ticker)
    ? ticker
        .toUpperCase()
        .replace(/[-_]/, '/')
        .split('/')
        .map((asset) => LEGACY_ASSETS[asset] ?? asset)
        .join('/')
    : null;

  const order = exchangeHint
    ? [exchangeHint]
    : [DEFAULT_EXCHANGE, ...EXCHANGES.map((e) => e.id).filter((id) => id !== DEFAULT_EXCHANGE)];

  let failed: string | null = null;
  let failedPair: SymbolRef | null = null;
  for (const exchangeId of order) {
    let pairs: TradingPair[];
    try {
      pairs = await loadPairs(provider, exchangeId);
    } catch {
      // Exchange unreachable from here; try the next one.
      failed ??= exchangeId;
      if (exchangeHint && explicitPair) failedPair = { exchangeId: exchangeHint, pair: explicitPair };
      continue;
    }
    let match = explicitPair ? pairs.find((p) => p.symbol === explicitPair) : undefined;
    for (const candidate of wanted) {
      if (match) break;
      match = pairs.find((p) => normalizeTicker(p.symbol) === candidate);
    }
    if (match) return { exchangeId, pair: match.symbol };
  }
  // An explicit BASE/QUOTE is still worth trying when that exchange's list failed to load.
  // When the list loaded without a match the pair does not exist.
  if (failedPair) return failedPair;
  if (failed) throw new Error(`Could not load the market list of ${failed}`);
  return null;
}
